// src/pages/connect/Lexware.js
import React, { useState, useEffect } from "react";
import { Card, Button, Alert, Modal, Table, Spinner, Form } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faPlug, faUpload } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const Lexware = () => {
  const [connected, setConnected] = useState(false);
  const [file, setFile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ====== CHECK STATUS ======
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get("/api/integrations/status/lexware");
        setConnected(res.data.connected);
      } catch (err) {
        console.error("Error checking Lexware status", err);
      }
    };
    checkStatus();
  }, []);

  // ====== UPLOAD FILE ======
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return alert("Please select a file first!");

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading(true);
      const res = await axios.post("/api/integrations/lexware/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConnected(true);
      alert(`${res.data.count} invoices uploaded from Lexware ✅`);
    } catch (err) {
      console.error("Error uploading Lexware file", err);
      alert("Upload failed ❌");
    } finally {
      setLoading(false);
    }
  };

  // ====== DISCONNECT ======
  const handleDisconnect = async () => {
    try {
      await axios.delete("/api/integrations/disconnect/lexware");
      setConnected(false);
      setInvoices([]);
    } catch (err) {
      console.error("Error disconnecting Lexware", err);
    }
  };

  // ====== VIEW INVOICES ======
  const handleViewInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/integrations/lexware/invoices");
      setInvoices(res.data.invoices || []);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching invoices", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <h3 className="mb-4">
        <FontAwesomeIcon icon={faPlug} className="me-2" />
        Connect Lexware Office
      </h3>

      <Card className="shadow-sm">
        <Card.Body>
          <FontAwesomeIcon icon={faBook} size="2x" className="mb-3 text-primary" />
          <Card.Title>Lexware Office</Card.Title>
          <Card.Text>
            Upload your Lexware invoice exports (Excel/CSV) to sync overdue invoices.
          </Card.Text>

          {connected ? (
            <>
              <Alert variant="success" className="mt-3">
                ✅ Connected successfully!
              </Alert>
              <div className="d-flex gap-2 mt-3">
                <Button variant="danger" onClick={handleDisconnect}>
                  Disconnect
                </Button>
                <Button variant="primary" onClick={handleViewInvoices} disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : "View Invoices"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Form.Group controlId="lexwareFile" className="mt-3">
                <Form.Control type="file" onChange={handleFileChange} />
              </Form.Group>
              <Button
                variant="primary"
                className="mt-2"
                onClick={handleUpload}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                {loading ? "Uploading..." : "Upload & Connect"}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ====== Invoices Modal ====== */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Lexware Invoices</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invoices.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr key={idx}>
                    <td>{inv.invoiceNumber || "-"}</td>
                    <td>{inv.customerName || "-"}</td>
                    <td>{inv.amount || "-"}</td>
                    <td>{inv.dueDate || "-"}</td>
                    <td>{inv.status || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No overdue invoices found ✅</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Lexware;
